import { usePermissionData } from "AppContext"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/Loader"
import moment from "moment"
import React, { useEffect, useState } from "react"
import { FaEdit, FaEye, FaFilter } from "react-icons/fa"
import { useParams } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  Row,
  Col,
} from "reactstrap"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"

/* ================= HELPERS ================= */

const toMoment = (date, time) => moment(`${date} ${time}`, "YYYY-MM-DD HH:mm")

const getFirstPunch = logs => {
  if (!logs?.length) return null
  return logs.reduce((earliest, curr) =>
    curr.inTime < earliest.inTime ? curr : earliest
  )
}

const getLastPunch = logs => {
  if (!logs?.length) return null
  return logs.reduce((latest, curr) =>
    curr.outTime > latest.outTime ? curr : latest
  )
}

const getTotalWorkedTime = (logs, date) => {
  if (!logs?.length) return "--"

  let totalMinutes = 0

  logs.forEach(log => {
    if (log.inTime && log.outTime) {
      const start = toMoment(date, log.inTime)
      const end = toMoment(date, log.outTime)

      if (end.isAfter(start)) {
        totalMinutes += end.diff(start, "minutes")
      }
    }
  })

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return `${hours}h ${minutes}m`
}

const PunchDetails = () => {
  const { id } = useParams()

  const ITEMS_PER_PAGE = 50
  const [loading, setLoading] = useState(false)

  const [allDetails, setAllDetails] = useState([])
  const [filteredDetails, setFilteredDetails] = useState([])
  const [currentPage, setCurrentPage] = useState(1)

  const [selectedMonth, setSelectedMonth] = useState(moment().format("MM"))
  const [selectedYear, setSelectedYear] = useState(moment().format("YYYY"))

  const [viewModal, setViewModal] = useState(false)
  const [selectedLogs, setSelectedLogs] = useState([])

  const [editModal, setEditModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState("")
  const [editData, setEditData] = useState({
    firstPunchId: null,
    lastPunchId: null,
    firstPunch: "",
    lastPunch: "",
  })

  /* ================= FETCH ================= */

  useEffect(() => {
    fetchPunches()
  }, [])

  useEffect(() => {
    applyMonthFilter()
  }, [selectedMonth, selectedYear, allDetails])

  const fetchPunches = async () => {
    const res = await FetchPostData(setLoading, "attendance/of/user/admin", {
      userId: id,
      month: selectedMonth,
      year: selectedYear,
    })
    if (res?.data?.success) {
      setAllDetails(res?.data?.data)
    }
  }

  /* ================= MONTH FILTER ================= */

  const applyMonthFilter = () => {
    const filtered = allDetails.filter(item => {
      const itemDate = moment(item.inDate)
      return (
        itemDate.format("MM") === selectedMonth &&
        itemDate.format("YYYY") === selectedYear
      )
    })

    setFilteredDetails(filtered)
    setCurrentPage(1)
  }

  /* ================= PAGINATION ================= */

  const paginatedData = filteredDetails.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  /* ================= VIEW ================= */

  const handleView = logs => {
    setSelectedLogs(logs || [])
    setViewModal(true)
  }

  console.log(selectedLogs)

  /* ================= EDIT ================= */

  const handleEdit = (logs, date) => {
    const first = logs?.length ? getFirstPunch(logs) : null
    const last = logs?.length ? getLastPunch(logs) : null

    setEditData({
      firstPunchId: first?.id,
      lastPunchId: last?.id,
      firstPunch: first?.inTime,
      lastPunch: last?.outTime,
    })

    setSelectedLogs(logs || [])
    setSelectedDate(date)
    setEditModal(true)
  }

  /* ================= SAVE EDIT (VALIDATION) ================= */

  const handleSaveEdit = async () => {
    // if (!editData?.firstPunch || !editData?.lastPunch) {
    //   return Swal.fire("Error", "Both times are required", "warning")
    // }

    if (!editData?.firstPunch || !editData?.lastPunch) {
      if (
        moment(editData?.firstPunch).isSameOrAfter(moment(editData?.lastPunch))
      ) {
        return Swal.fire("Error", "Punch-out must be after punch-in", "error")
      }
    }

    const APIdata = await FetchPutData(setLoading, "attendance/update", id, {
      ...editData,
      selectedDate,
    })

    if (APIdata?.data?.success) {
      Swal.fire("Success", APIdata?.data?.message, "success")
      setTimeout(() => {
        fetchPunches()
      }, 1500)
      setEditModal(false)
    } else {
      Swal.fire("Error", APIdata?.response?.data?.message, "error")
    }
  }

  const { checkAccess } = usePermissionData()

  return (
    <div className="page-content">
      {loading && <Loader />}
      <Breadcrumb title="Payroll" breadcrumbItem="Employee Punch Details" />

      <Card className="mb-3">
        <CardBody>
          <div className="d-flex align-items-center justify-content-between">
            {/* ====== FILTER SECTION ====== */}
            <h4 className="fw-bold mb-0">
              {paginatedData?.[0]?.user} Punch Details
            </h4>
            <Row className="w-50 align-items-end justify-content-end">
              <Col md="4">
                <Label>Select Month</Label>
                <Input
                  type="select"
                  value={selectedMonth}
                  onChange={e => setSelectedMonth(e.target.value)}
                >
                  {moment.months().map((month, index) => (
                    <option
                      key={index}
                      value={moment().month(index).format("MM")}
                    >
                      {month}
                    </option>
                  ))}
                </Input>
              </Col>

              <Col md="4">
                <Label>Select Year</Label>
                <Input
                  type="select"
                  value={selectedYear}
                  onChange={e => setSelectedYear(e.target.value)}
                >
                  {[2024, 2025, 2026, 2027].map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Input>
              </Col>
            </Row>
          </div>

          <div
            style={{
              maxHeight: "70vh",
              overflowY: "auto",
              marginTop: "1.5rem",
            }}
          >
            <Table striped className="mb-0" style={{ tableLayout: "fixed" }}>
              <thead
                className="table-dark"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <tr>
                  <td>#</td>
                  <td>Date</td>
                  <td>First Punch</td>
                  <td>Last Punch</td>
                  <td>Total Time</td>
                  <td>Status</td>
                  <td>Action</td>
                </tr>
              </thead>

              <tbody>
                {paginatedData.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center">
                      No Records Found
                    </td>
                  </tr>
                )}

                {paginatedData.map((item, index) => {
                  const first = getFirstPunch(item.logs)
                  const last = getLastPunch(item.logs)

                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{moment(item.inDate).format("DD-MM-YYYY")}</td>
                      <td>
                        {first?.inTime
                          ? moment(first.inTime, "HH:mm:ss").format("hh:mm A")
                          : "--"}
                      </td>

                      <td>
                        {last?.outTime
                          ? moment(last.outTime, "HH:mm:ss").format("hh:mm A")
                          : "--"}
                      </td>
                      <td>{getTotalWorkedTime(item.logs, item.inDate)}</td>
                      <td>
                        {first
                          ? first.inTime
                            ? "Present"
                            : "Absent"
                          : "Absent"}
                      </td>
                      <td>
                        <Button
                          color="secondary"
                          onClick={() => handleView(item.logs)}
                        >
                          <FaEye />
                        </Button>
                        {checkAccess(14, "edit") && (
                          <Button
                            color="warning"
                            onClick={() => handleEdit(item.logs, item.inDate)}
                            className="ms-2 cursor-pointer"
                          >
                            <FaEdit />
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* ===== VIEW MODAL ===== */}

      <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="xl">
        <ModalHeader toggle={() => setViewModal(false)}>
          All Punches
        </ModalHeader>
        <ModalBody>
          <Table striped>
            <thead className="table-dark">
              <tr>
                <td>#</td>
                <td>Punch In</td>
                <td>In Address</td>
                <td>Punch Out</td>
                <td>Out Address</td>
              </tr>
            </thead>
            <tbody>
              {selectedLogs?.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center">
                    No Records Found
                  </td>
                </tr>
              )}
              {selectedLogs?.map((log, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{log.inTime || "--"}</td>
                  <td>{log.punchInAddress || "--"}</td>
                  <td>{log.outTime || "--"}</td>
                  <td>{log.punchOutAddress || "--"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ModalBody>
      </Modal>

      {/* ================= EDIT MODAL ================= */}

      <Modal isOpen={editModal} toggle={() => setEditModal(false)}>
        <ModalHeader toggle={() => setEditModal(false)}>Edit Punch</ModalHeader>
        <ModalBody>
          <Label>In Time</Label>
          <Input
            type="time"
            value={editData.firstPunch}
            onChange={e =>
              setEditData({ ...editData, firstPunch: e.target.value })
            }
          />
          <Label className="mt-4">Out Time</Label>
          <Input
            type="time"
            value={editData.lastPunch}
            onChange={e =>
              setEditData({ ...editData, lastPunch: e.target.value })
            }
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSaveEdit}>
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default PunchDetails
